/* Vini's Ethnic – app-free infinite scroll for Dawn collection pages
   Back-button restore: when a saree is tapped, the sarees already loaded are
   saved in the tab's session storage. On coming back, the inline script in
   snippets/infinite-scroll.liquid puts them straight back and scrolls to the
   tapped saree before the page is shown – no reloading, no spinner.
   If that saved copy isn't available, it falls back to re-fetching the pages. */
if (!customElements.get('infinite-scroll')) {
  const STORAGE_PREFIX = 'vinis-infinite-scroll:';
  const MAX_AGE_MS = 20 * 60 * 1000; // must match the inline script in the snippet
  const state = { pages: 1, firstPage: 1, grid: null, restoreChecked: false, lastClickSave: 0 };

  // One saved position per collection + filter/sort combination
  const listKey = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return STORAGE_PREFIX + url.pathname + url.search;
  };

  const writePosition = (data) => {
    try {
      if (data.pages <= state.firstPage) {
        // Only the first page is involved – the browser restores this by itself
        sessionStorage.removeItem(listKey());
        return;
      }
      data.savedAt = Date.now();
      try {
        sessionStorage.setItem(listKey(), JSON.stringify(data));
      } catch (quotaError) {
        // Too big to keep a copy of the sarees – save the position only
        delete data.html;
        sessionStorage.setItem(listKey(), JSON.stringify(data));
      }
    } catch (e) { /* storage unavailable – skip quietly */ }
  };

  const readPosition = () => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(listKey()));
      if (saved && Date.now() - saved.savedAt < MAX_AGE_MS) return saved;
    } catch (e) { /* ignore */ }
    return null;
  };

  const isBackNavigation = () => {
    const entries = performance.getEntriesByType ? performance.getEntriesByType('navigation') : [];
    if (entries.length) return entries[0].type === 'back_forward';
    return performance.navigation && performance.navigation.type === 2;
  };

  const endRestoring = (restored) => {
    const root = document.documentElement;
    root.classList.remove('infinite-scroll-restoring');
    if (restored) {
      root.classList.add('infinite-scroll-restored');
    } else if ('scrollRestoration' in history) {
      history.scrollRestoration = 'auto';
    }
  };

  // Collections use <ul id="product-grid">; the search page wraps the <ul> in
  // <div id="product-grid">. Always work with the list that holds the cards.
  const findList = (root) => {
    const grid = root.querySelector('#product-grid');
    if (!grid) return null;
    return grid.matches('ul') ? grid : grid.querySelector('ul.product-grid') || grid;
  };

  const onCurrentGrid = () => state.grid && document.getElementById('product-grid') === state.grid;

  // Save everything needed to rebuild the list instantly
  const snapshot = (extra) => {
    if (!onCurrentGrid()) {
      writePosition({ pages: 1 });
      return;
    }
    const list = findList(document);
    const added = list ? list.querySelectorAll(':scope > .grid__item[data-page]') : [];
    const loader = document.querySelector('infinite-scroll');
    writePosition(Object.assign({
      pages: state.pages,
      nextUrl: loader ? loader.dataset.nextUrl || '' : '',
      html: Array.prototype.map.call(added, (node) => node.outerHTML).join(''),
      scrollY: window.scrollY
    }, extra));
  };

  // When a saree is tapped: also remember which one and where it sat on screen
  document.addEventListener('click', (event) => {
    const link = event.target.closest('#product-grid a[href]');
    if (!link) return;
    const item = link.closest('.grid__item');
    snapshot({
      href: link.getAttribute('href'),
      top: item ? item.getBoundingClientRect().top : null
    });
    state.lastClickSave = Date.now();
  }, true);

  // Leaving some other way (e.g. menu link): remember the list and scroll position
  window.addEventListener('pagehide', () => {
    if (Date.now() - state.lastClickSave < 5000) return; // keep the saree-based save
    snapshot({});
  });

  class InfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.loading = false;
      this.grid = document.getElementById('product-grid');
      state.grid = this.grid;
      this.list = this.grid ? findList(document) : null;
      state.firstPage = state.pages = parseInt(this.dataset.currentPage, 10) || 1;

      if (!this.grid) {
        this.remove();
        return;
      }

      this.pagination = this.parentElement.querySelector('.pagination-wrapper');

      // Instant restore already done by the inline script – just pick up from there
      const instant = !state.restoreChecked && window.__vinisInfiniteRestore;
      if (instant) {
        state.restoreChecked = true;
        window.__vinisInfiniteRestore = null;
        state.pages = instant.pages;
        this.dataset.nextUrl = instant.nextUrl || '';
        endRestoring(true);
        if (this.pagination) this.pagination.hidden = true;
      }

      if (!this.dataset.nextUrl) {
        if (!state.restoreChecked) { state.restoreChecked = true; endRestoring(false); }
        this.remove();
        return;
      }

      // Keep pagination links in the HTML for Google, hide them for shoppers
      if (this.pagination) this.pagination.hidden = true;

      this.observer = new IntersectionObserver(
        (entries) => { if (entries[0].isIntersecting) this.loadNext(); },
        { rootMargin: '800px 0px' }
      );

      // Fallback restore (no saved copy): re-fetch the pages
      if (!state.restoreChecked) {
        state.restoreChecked = true;
        const saved = isBackNavigation() ? readPosition() : null;
        if (saved && saved.pages > state.pages) {
          this.restore(saved);
          return;
        }
        endRestoring(false);
      }

      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    pageUrl(pageNumber) {
      const url = new URL(this.dataset.nextUrl, window.location.origin);
      url.searchParams.set('page', pageNumber);
      url.searchParams.set('section_id', this.grid.dataset.id);
      return url.toString();
    }

    async fetchPage(pageNumber) {
      const response = await fetch(this.pageUrl(pageNumber));
      if (!response.ok) throw new Error('Page ' + pageNumber + ' returned ' + response.status);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    appendPage(doc, pageNumber) {
      const newList = findList(doc);
      const items = newList ? newList.querySelectorAll(':scope > .grid__item') : [];
      if (!items.length) throw new Error('No products found on page ' + pageNumber);
      items.forEach((item) => {
        const node = document.importNode(item, true);
        node.dataset.page = pageNumber;
        this.list.appendChild(node);
      });
      state.pages = pageNumber;
      const next = doc.querySelector('infinite-scroll');
      this.dataset.nextUrl = next && next.dataset.nextUrl ? next.dataset.nextUrl : '';
    }

    async restore(saved) {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      document.documentElement.classList.add('infinite-scroll-restoring');

      try {
        const pageNumbers = [];
        for (let p = state.pages + 1; p <= saved.pages; p++) pageNumbers.push(p);
        const docs = await Promise.all(pageNumbers.map((p) => this.fetchPage(p)));
        docs.forEach((doc, i) => this.appendPage(doc, pageNumbers[i]));
      } catch (error) {
        this.fail(error);
        return;
      }

      requestAnimationFrame(() => requestAnimationFrame(() => {
        let y = saved.scrollY;
        if (saved.href && saved.top !== null && saved.top !== undefined) {
          const link = this.grid.querySelector('a[href="' + CSS.escape(saved.href) + '"]');
          const item = link && link.closest('.grid__item');
          if (item) y = item.getBoundingClientRect().top + window.scrollY - saved.top;
        }
        window.scrollTo(0, Math.max(0, y));
        endRestoring(true);
      }));

      if (this.dataset.nextUrl) this.observer.observe(this);
      else this.finish();
    }

    async loadNext() {
      if (this.loading || !this.dataset.nextUrl) return;
      this.loading = true;
      this.classList.add('is-loading');

      try {
        const pageNumber = state.pages + 1;
        const doc = await this.fetchPage(pageNumber);
        this.appendPage(doc, pageNumber);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }
      } catch (error) {
        this.fail(error);
        return;
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
      }

      if (this.dataset.nextUrl) {
        this.observer.unobserve(this);
        this.observer.observe(this);
      } else {
        this.finish();
      }
    }

    // Fall back to normal pagination so shoppers are never stuck
    fail(error) {
      console.error('Infinite scroll:', error);
      if (this.pagination) this.pagination.hidden = false;
      endRestoring(false);
      this.finish();
    }

    finish() {
      if (this.observer) this.observer.disconnect();
      this.remove();
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}
